if (!customElements.get('search-redirect')) {
  const CACHE_TTL = 60 * 60 * 1000;
  const SUBMIT_TIMEOUT = 1000;

  class SearchRedirect extends HTMLElement {
    constructor() {
      super();
      this.rootUrl = (this.dataset.rootUrl || '/').replace(/\/$/, '');
      this.searchUrl = this.dataset.searchUrl || '/search';
      // Keyed by locale path (not just language): cached target URLs include that prefix.
      this.cacheKey = `search_redirects:${this.rootUrl || '/'}`;
      this.redirects = null;
      this.loading = null;

      this.onFocusIn = this.onFocusIn.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      // search-form.liquid is rendered multiple times per page; only one instance handles events.
      if (SearchRedirect.active && SearchRedirect.active !== this) return;
      SearchRedirect.active = this;

      document.addEventListener('focusin', this.onFocusIn);
      // Capture phase so we run before (and can stop) the search form's own submit handlers.
      document.addEventListener('submit', this.onSubmit, true);
    }

    disconnectedCallback() {
      if (SearchRedirect.active !== this) return;
      SearchRedirect.active = null;

      document.removeEventListener('focusin', this.onFocusIn);
      document.removeEventListener('submit', this.onSubmit, true);
    }

    isSearchForm(form) {
      return form instanceof HTMLFormElement && form.getAttribute('action') === this.searchUrl;
    }

    onFocusIn(event) {
      if (event.target.matches?.('input[type="search"]') && this.isSearchForm(event.target.form)) {
        this.load();
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (!this.isSearchForm(form)) return;
      if (this.bypassNextSubmit) {
        this.bypassNextSubmit = false;
        return;
      }

      const searchValue = form.querySelector('input[type="search"]')?.value.toLowerCase().trim();
      if (!searchValue) return;

      if (this.redirects) {
        const redirect = this.redirects.get(searchValue);
        if (redirect) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.redirectTo(redirect);
        }
        return;
      }

      // Data not loaded yet: hold the submit briefly, then redirect or search as usual.
      event.preventDefault();
      event.stopImmediatePropagation();

      const timeout = new Promise((resolve) => setTimeout(resolve, SUBMIT_TIMEOUT));
      Promise.race([this.load(), timeout]).then(() => {
        const redirect = this.redirects?.get(searchValue);
        if (redirect) {
          this.redirectTo(redirect);
        } else {
          this.resubmit(form);
        }
      });
    }

    resubmit(form) {
      this.bypassNextSubmit = true;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }

    redirectTo({ url, collection }) {
      if (!collection || sessionStorage.getItem('collectionAvailabilityFilterDeselected') === '1') {
        window.location.href = url;
        return;
      }

      const [path, hash] = url.split('#');
      const availabilityParam = (path.includes('?') ? '&' : '?') + 'filter.v.availability=1';
      window.location.href = path + availabilityParam + (hash ? `#${hash}` : '');
    }

    load() {
      if (this.redirects) return Promise.resolve(this.redirects);
      if (this.loading) return this.loading;

      const cached = this.readCache();
      if (cached) {
        this.redirects = this.toMap(cached);
        return Promise.resolve(this.redirects);
      }

      this.loading = this.fetchPage()
        .then(({ pages, param, items }) => {
          const rest = [];
          for (let page = 2; page <= pages; page++) rest.push(this.fetchPage(param, page));
          return Promise.all(rest).then((results) =>
            items.concat(...results.map((result) => result.items))
          );
        })
        .then((items) => {
          // Don't cache an empty result (e.g. metaobject not readable yet), so it's retried next page view.
          if (items.length) this.writeCache(items);
          this.redirects = this.toMap(items);
          return this.redirects;
        })
        .catch((error) => {
          console.error('[search-redirect] Failed to load redirects', error);
          this.redirects = new Map();
          return this.redirects;
        })
        .finally(() => {
          this.loading = null;
        });

      return this.loading;
    }

    // Metaobject pagination uses its own URL param (e.g. `page_bc3b138b`), returned by page 1 as `param`.
    fetchPage(param, page) {
      const pageQuery = param ? `&${param}=${page}` : '';
      return fetch(`${this.rootUrl}/?section_id=search-redirects${pageQuery}`)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const script = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-search-redirects]');
          return script ? JSON.parse(script.textContent) : { pages: 1, items: [] };
        });
    }

    toMap(items) {
      const map = new Map();
      items.forEach(({ q, url, collection }) => {
        if (!map.has(q)) map.set(q, { url, collection });
      });
      return map;
    }

    readCache() {
      try {
        const cached = JSON.parse(sessionStorage.getItem(this.cacheKey));
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.items;
      } catch (error) {
        // sessionStorage unavailable or corrupt: fall back to fetching.
      }
      return null;
    }

    writeCache(items) {
      try {
        // Drop the legacy (non-localized) cache of the old collection_redirects setup.
        sessionStorage.removeItem('collection_redirects');
        sessionStorage.setItem(this.cacheKey, JSON.stringify({ timestamp: Date.now(), items }));
      } catch (error) {
        // sessionStorage unavailable or full: redirects still work for this page view.
      }
    }
  }

  window.SearchRedirect = SearchRedirect;

  customElements.define('search-redirect', SearchRedirect);
}
